import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { FanContentNotice } from "@/components/layout/FanContentNotice";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import Home from "@/pages/Home";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import AuthCallback from "@/pages/auth/AuthCallback";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Library from "@/pages/Library";
import CardDetail from "@/pages/CardDetail";
import CardEditor from "@/pages/CardEditor";
import UploadPlanar from "@/pages/UploadPlanar";
import BulkUpload from "@/pages/BulkUpload";
import Sets from "@/pages/Sets";
import SetDetail from "@/pages/SetDetail";
import CardBacks from "@/pages/CardBacks";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Dashboard from "@/pages/Dashboard";
import Staff from "@/pages/Staff";
import MyModeration from "@/pages/MyModeration";
import { SiteAnnouncements } from "@/components/layout/SiteAnnouncements";
import Roadmap from "@/pages/Roadmap";
import NotFound from "@/pages/NotFound";
import Decks from "@/pages/Decks";
import DeckBuilder from "@/pages/DeckBuilder";
import DeckView from "@/pages/DeckView";
import OfficialCards from "@/pages/OfficialCards";
import Communities from "@/pages/Communities";
import DeveloperApi from "@/pages/DeveloperApi";
import DevApps from "@/pages/developers/DevApps";
import DevAppDetail from "@/pages/developers/DevAppDetail";
import DevDocs from "@/pages/developers/DevDocs";
import DevUsage from "@/pages/developers/DevUsage";
import DevExplorer from "@/pages/developers/DevExplorer";
import OAuthAuthorize from "@/pages/OAuthAuthorize";
import { ReauthDialog } from "@/components/developer/ReauthDialog";
import Formats from "@/pages/Formats";
import Mechanics from "@/pages/Mechanics";
import MechanicDetail from "@/pages/MechanicDetail";
import CommunityPage from "@/pages/CommunityPage";
import CommunityApplications from "@/pages/CommunityApplications";
import Tournaments from "@/pages/Tournaments";
import TournamentPage from "@/pages/TournamentPage";
import Leagues from "@/pages/Leagues";
import LeaguePage from "@/pages/LeaguePage";
import CompanionHub from "@/pages/CompanionHub";
import CompanionSession from "@/pages/CompanionSession";
import CompanionDisplay from "@/pages/CompanionDisplay";
import ImportHub from "@/pages/ImportHub";
import ImportDraft from "@/pages/ImportDraft";
import "@/App.css";

const guard = (el, permission) => <ProtectedRoute permission={permission}>{el}</ProtectedRoute>;

function AppRouter() {
  const location = useLocation();
  // Google OAuth returns with #session_id=... — exchange it before any route renders.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/library" element={<Library />} />
      <Route path="/cards/:cardId" element={<CardDetail />} />
      <Route path="/cards/:cardId/edit" element={guard(<CardEditor />)} />
      <Route path="/cards/:cardId/edit-planar" element={guard(<UploadPlanar />)} />
      <Route path="/upload/planar" element={guard(<UploadPlanar />)} />
      <Route path="/upload" element={guard(<CardEditor />)} />
      <Route path="/upload/bulk" element={guard(<BulkUpload />)} />
      <Route path="/import" element={guard(<ImportHub />)} />
      <Route path="/import/:draftId" element={guard(<ImportDraft />)} />
      <Route path="/sets" element={<Sets />} />
      <Route path="/sets/:setId" element={<SetDetail />} />
      <Route path="/card-backs" element={<CardBacks />} />
      <Route path="/u/:username" element={<Profile />} />
      <Route path="/settings" element={guard(<Settings />)} />
      <Route path="/dashboard" element={guard(<Dashboard />)} />
      <Route path="/staff" element={guard(<Staff />, "users.view")} />
      <Route path="/me/moderation" element={guard(<MyModeration />)} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/official" element={<OfficialCards />} />
      <Route path="/decks" element={<Decks />} />
      <Route path="/decks/:deckId" element={<DeckView />} />
      <Route path="/decks/:deckId/build" element={guard(<DeckBuilder />)} />
      <Route path="/communities" element={<Communities />} />
      <Route path="/communities/applications" element={guard(<CommunityApplications />)} />
      <Route path="/c/:slug" element={<CommunityPage />} />
      <Route path="/companion" element={<CompanionHub />} />
      <Route path="/companion/s/:code" element={guard(<CompanionSession />)} />
      <Route path="/companion/s/:code/display" element={guard(<CompanionDisplay />)} />
      <Route path="/tournaments" element={<Tournaments />} />
      <Route path="/t/:tid" element={<TournamentPage />} />
      <Route path="/leagues" element={<Leagues />} />
      <Route path="/l/:lid" element={<LeaguePage />} />
      <Route path="/formats" element={<Formats />} />
      <Route path="/mechanics" element={<Mechanics />} />
      <Route path="/mechanics/:mechanicId" element={<MechanicDetail />} />
      <Route path="/developers" element={<DeveloperApi />} />
      <Route path="/developers/apps" element={<DevApps />} />
      <Route path="/developers/apps/:appId" element={<DevAppDetail />} />
      <Route path="/developers/docs" element={<DevDocs />} />
      <Route path="/developers/usage" element={<DevUsage />} />
      <Route path="/developers/explorer" element={<DevExplorer />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function Chrome() {
  const bare = useLocation().pathname.endsWith("/display");
  return (
    <div className="grain relative flex min-h-screen flex-col">
      {!bare && <Navbar />}
      {!bare && <SiteAnnouncements />}
      <main className="relative z-10 flex-1">
        <AppRouter />
      </main>
      {bare ? <FanContentNotice compact /> : <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Chrome />
        <Toaster theme="dark" position="bottom-right" richColors />
        <ReauthDialog />
      </AuthProvider>
    </BrowserRouter>
  );
}
